/**
 * Querying the vault by frontmatter.
 *
 * This is what makes the HUD extensible without code. Rather than a `goals`
 * widget and a `books` widget and a `movies` widget, there is one `collection`
 * widget that runs a query and renders the results — so tracking something new
 * means adding markdown files and one config, never a pull request.
 *
 * Queries run against a {@link VaultIndex}: every markdown file's path plus its
 * parsed frontmatter. Building that index by reading the whole vault is free
 * locally but is N requests over the GitHub API, so in production the index is
 * loaded from a manifest precomputed by an Actions workflow. Both produce the
 * same shape, and nothing above this line can tell which one it got.
 */

import { parseFile } from './markdown.js';
import type { VaultFS } from './ports.js';

export interface IndexEntry {
  path: string;
  /** Parsed frontmatter. Empty for files that have none. */
  data: Record<string, unknown>;
  /** Body text, present only when the index was built with `includeBody`. */
  body?: string;
}

export interface VaultIndex {
  entries: IndexEntry[];
}

// ─── Building ───────────────────────────────────────────────────────────────

export interface BuildIndexOptions {
  /** Restrict the scan. Defaults to every markdown file. */
  include?: (path: string) => boolean;
  includeBody?: boolean;
}

/**
 * Read every markdown file and parse its frontmatter.
 *
 * Deliberately not incremental: correctness first, and the M3 GitHub adapter
 * will supply a precomputed manifest instead of calling this at all.
 */
export async function buildIndex(fs: VaultFS, options: BuildIndexOptions = {}): Promise<VaultIndex> {
  const include = options.include ?? ((path: string) => path.endsWith('.md'));
  const files = (await fs.list()).filter((entry) => include(entry.path));

  const entries = await Promise.all(
    files.map(async (file): Promise<IndexEntry | null> => {
      try {
        const { content } = await fs.read(file.path);
        const parsed = parseFile(content);
        return {
          path: file.path,
          data: parsed.data,
          ...(options.includeBody ? { body: parsed.body } : {}),
        };
      } catch {
        // A file that vanished mid-scan shouldn't fail the whole dashboard.
        return null;
      }
    }),
  );

  return { entries: entries.filter((entry): entry is IndexEntry => entry !== null) };
}

// ─── Glob matching ──────────────────────────────────────────────────────────

const GLOB_CACHE = new Map<string, RegExp>();

/**
 * Minimal glob: `**` crosses directories, `*` and `?` do not.
 * `books/**` and `books/*` both match `books/dune.md`.
 *
 * Scanned character by character rather than via chained `.replace` calls — a
 * substitution pass has to smuggle already-converted tokens past later passes,
 * and any sentinel chosen for that can itself appear in a real path.
 */
export function globToRegExp(glob: string): RegExp {
  const cached = GLOB_CACHE.get(glob);
  if (cached) return cached;

  const REGEX_SPECIALS = new Set(['.', '+', '^', '$', '{', '}', '(', ')', '|', '[', ']', '\\']);

  let pattern = '';
  for (let i = 0; i < glob.length; i++) {
    const char = glob[i]!;

    if (char === '*') {
      if (glob[i + 1] === '*') {
        pattern += '.*';
        i += 1;
      } else {
        pattern += '[^/]*';
      }
      continue;
    }

    if (char === '?') {
      pattern += '[^/]';
      continue;
    }

    pattern += REGEX_SPECIALS.has(char) ? `\\${char}` : char;
  }

  const regexp = new RegExp(`^${pattern}$`);
  GLOB_CACHE.set(glob, regexp);
  return regexp;
}

// ─── Predicates ─────────────────────────────────────────────────────────────

export interface PredicateOps {
  eq?: unknown;
  ne?: unknown;
  gt?: unknown;
  gte?: unknown;
  lt?: unknown;
  lte?: unknown;
  in?: unknown[];
  nin?: unknown[];
  /** `true` requires the field be present and non-null; `false` requires absent. */
  exists?: boolean;
  /** Substring match for strings, membership for arrays. Case-insensitive. */
  contains?: string;
  /** Regular expression source, case-insensitive. */
  matches?: string;
}

export type Predicate = string | number | boolean | null | PredicateOps;

export type SortDirection = 'asc' | 'desc';

export interface SortSpec {
  by: string;
  dir?: SortDirection;
}

export interface Query {
  /** Glob over vault paths, e.g. `books/**`. */
  from: string;
  where?: Record<string, Predicate>;
  sort?: string | SortSpec | (string | SortSpec)[];
  limit?: number;
}

const OP_KEYS = new Set([
  'eq',
  'ne',
  'gt',
  'gte',
  'lt',
  'lte',
  'in',
  'nin',
  'exists',
  'contains',
  'matches',
]);

function isPredicateOps(value: Predicate): value is PredicateOps {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) return false;
  const keys = Object.keys(value);
  return keys.length > 0 && keys.every((key) => OP_KEYS.has(key));
}

/** Read `a.b.c` out of nested frontmatter. */
export function getField(data: Record<string, unknown>, field: string): unknown {
  if (!field.includes('.')) return data[field];

  let current: unknown = data;
  for (const part of field.split('.')) {
    if (current === null || typeof current !== 'object') return undefined;
    current = (current as Record<string, unknown>)[part];
  }
  return current;
}

/** Loose equality that treats YAML's stringy dates and mixed case sensibly. */
function looseEquals(actual: unknown, expected: unknown): boolean {
  if (actual === expected) return true;
  if (actual instanceof Date && typeof expected === 'string') {
    return actual.toISOString().startsWith(expected);
  }
  if (Array.isArray(actual)) return actual.some((item) => looseEquals(item, expected));
  if (typeof actual === 'string' && typeof expected === 'string') {
    return actual.toLowerCase() === expected.toLowerCase();
  }
  return false;
}

/** Ordering across strings, numbers, booleans, and dates. Missing values sort last. */
export function compareValues(a: unknown, b: unknown): number {
  const aMissing = a === undefined || a === null;
  const bMissing = b === undefined || b === null;
  if (aMissing && bMissing) return 0;
  if (aMissing) return 1;
  if (bMissing) return -1;

  const av = a instanceof Date ? a.getTime() : a;
  const bv = b instanceof Date ? b.getTime() : b;

  if (typeof av === 'number' && typeof bv === 'number') return av - bv;
  if (typeof av === 'boolean' && typeof bv === 'boolean') return Number(av) - Number(bv);

  return String(av).localeCompare(String(bv), undefined, { numeric: true, sensitivity: 'base' });
}

function matchesPredicate(actual: unknown, predicate: Predicate): boolean {
  if (!isPredicateOps(predicate)) return looseEquals(actual, predicate);

  const ops = predicate;

  if (ops.exists !== undefined) {
    const present = actual !== undefined && actual !== null;
    if (present !== ops.exists) return false;
  }
  if (ops.eq !== undefined && !looseEquals(actual, ops.eq)) return false;
  if (ops.ne !== undefined && looseEquals(actual, ops.ne)) return false;

  // Comparisons need different missing-value semantics than sorting does.
  // `compareValues` ranks missing LAST (returns 1) so unrated books sink to the
  // bottom of a sort — but reusing that here would make `rating: { gte: 5 }`
  // evaluate `1 < 0` and silently ACCEPT a book with no rating at all. A value
  // that isn't there can't satisfy a comparison, in either direction.
  const comparable = actual !== undefined && actual !== null;
  if (ops.gt !== undefined && (!comparable || compareValues(actual, ops.gt) <= 0)) return false;
  if (ops.gte !== undefined && (!comparable || compareValues(actual, ops.gte) < 0)) return false;
  if (ops.lt !== undefined && (!comparable || compareValues(actual, ops.lt) >= 0)) return false;
  if (ops.lte !== undefined && (!comparable || compareValues(actual, ops.lte) > 0)) return false;
  if (ops.in !== undefined && !ops.in.some((item) => looseEquals(actual, item))) return false;
  if (ops.nin !== undefined && ops.nin.some((item) => looseEquals(actual, item))) return false;

  if (ops.contains !== undefined) {
    const needle = ops.contains.toLowerCase();
    const hit = Array.isArray(actual)
      ? actual.some((item) => String(item).toLowerCase().includes(needle))
      : String(actual ?? '')
          .toLowerCase()
          .includes(needle);
    if (!hit) return false;
  }

  if (ops.matches !== undefined) {
    try {
      if (!new RegExp(ops.matches, 'i').test(String(actual ?? ''))) return false;
    } catch {
      return false;
    }
  }

  return true;
}

// ─── Running ────────────────────────────────────────────────────────────────

function normaliseSort(sort: Query['sort']): SortSpec[] {
  if (!sort) return [];
  const list = Array.isArray(sort) ? sort : [sort];
  return list.map((spec) => (typeof spec === 'string' ? { by: spec, dir: 'asc' as const } : spec));
}

export function runQuery(index: VaultIndex, query: Query): IndexEntry[] {
  const pattern = globToRegExp(query.from);

  let results = index.entries.filter((entry) => pattern.test(entry.path));

  if (query.where) {
    const conditions = Object.entries(query.where);
    results = results.filter((entry) =>
      conditions.every(([field, predicate]) =>
        matchesPredicate(getField(entry.data, field), predicate),
      ),
    );
  }

  const sorts = normaliseSort(query.sort);
  if (sorts.length > 0) {
    results = [...results].sort((a, b) => {
      for (const spec of sorts) {
        const order = compareValues(getField(a.data, spec.by), getField(b.data, spec.by));
        if (order !== 0) return spec.dir === 'desc' ? -order : order;
      }
      return 0;
    });
  }

  if (query.limit !== undefined && query.limit >= 0) {
    results = results.slice(0, query.limit);
  }

  return results;
}

/** `books/the-dune-saga.md` → `the-dune-saga`. Used as a title fallback. */
export function basename(path: string): string {
  const file = path.slice(path.lastIndexOf('/') + 1);
  return file.endsWith('.md') ? file.slice(0, -3) : file;
}
